import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { PaymentConfirmationComponent } from 'src/app/component/payment-confirmation/payment-confirmation.component';
import { ReviewComponent } from 'src/app/component/review/review.component';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { EventService } from 'src/app/services/event.service';
import { LoadingService } from 'src/app/services/loading.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { BillingService } from '@shared/components/billing-info/services/billing.service';
import { BillingSelectionComponent } from '@shared/components/billing-selection/billing-selection.component';
import { Deposit, Payment, PaymentMethod } from '@shared/models/payment.model';
import { getMappedPaymentMethod } from '@shared/utils';

@Component({
  selector: 'app-transactions-list',
  templateUrl: './transactions-list.page.html',
  styleUrls: ['./transactions-list.page.scss'],
})
export class TransactionsListPage implements OnInit {
  show = 'abonos';
  user;
  depositsPage = 1;
  paymentsPage = 1;
  depositsLoaded = 0;
  paymentsLoaded = 0;
  loading: boolean;
  period: string = null;
  business: number = null;
  businesses;
  deposits: Deposit[] = [];
  payments: Payment[] = [];

  constructor(
    private modalCtrl: ModalController,
    private auth: AuthService,
    private apiService: ApiService,
    private eventService: EventService,
    private dataService: DataService,
    private router: Router,
    private actionSheetCtrl: ActionSheetController,
    private loadingService: LoadingService,
    private billingService: BillingService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.auth.getUser().then((user) => {
      this.user = user;
      this.getDeposits();
      this.getPayments();
    });
  }

  doRefresh(infiniteScroll) {
    this.depositsPage = 1;
    this.paymentsPage = 1;
    this.getDeposits().then(() => {
      infiniteScroll.target.complete();
    });
    this.getPayments().then(() => {
      infiniteScroll.target.complete();
    });
  }

  doInfiniteSales(infiniteScroll) {
    this.getDeposits().then(() => {
      infiniteScroll.target.complete();
    });
  }

  querySales() {
    this.depositsLoaded = 0;
    this.depositsPage = 1;
    this.paymentsPage = 1;
    this.getDeposits();
  }

  getDeposits() {
    this.loading = true;
    return new Promise((resolve) => {
      this.apiService
        .getData('deposits?period=' + this.period + '&business_id=' + this.business + '&page=' + this.depositsPage)
        .then(
          (res) => {
            if (res && res.data) {
              if (res.current_page === 1) {
                this.deposits = res.data.map((deposit: Deposit) => {
                  deposit.method_name = getMappedPaymentMethod(deposit.method);
                  return deposit;
                });
              } else {
                if (this.depositsLoaded !== res.last_page) {
                  for (const i in res.data) {
                    if (res.data.hasOwnProperty(i)) {
                      res.data[i].method_name = getMappedPaymentMethod(res.data[i].method);
                      this.deposits.push(res.data[i]);
                    }
                  }
                }
              }
              if (res.current_page !== res.last_page) {
                this.depositsPage += 1;
              }
              this.depositsLoaded = res.current_page;
            }
            resolve(true);
          },
          (err) => {
            resolve(false);
          }
        )
        .finally(() => {
          this.loading = false;
        });
    });
  }

  async goHelp(deposit: Deposit) {
    if (deposit.status && deposit.invoiced === null) {
      const actionSheet: HTMLIonActionSheetElement = await this.actionSheetCtrl.create({
        header: 'Ayuda',
        buttons: [
          {
            text: 'Solicitar Factura',
            handler: () => {
              this.selectBilling(deposit.id, deposit.type);
            },
          },
          {
            text: 'Otro',
            handler: () => {
              this.router.navigate(['/tecservice-transportec/wallet/transactions/' + deposit.id + '/help/other2']);
            },
          },
        ],
      });
      actionSheet.present();
    } else {
      this.router.navigate(['/tecservice-transportec/wallet/transactions/' + deposit.id + '/help/other2']);
    }
  }

  requestInvoice(depositId: number, billingId: number, type: string) {
    this.billingService
      .createInvoice({
        billing_id: billingId,
        saldo_payment_id: type === 'saldo' ? depositId : null,
        suscription_payment_id: type === 'suscription' ? depositId : null,
      })
      .pipe(
        tap(() => {
          this.notificationService.presentToast('Se ha solicitado la factura correctamente', 3000, 'top');
          this.querySales();
        }),
        catchError((error) => {
          const message =
            error.error.message || 'Ha ocurrido un error al solicitar factura. Favor de intentar nuevamente.';
          this.notificationService.presentToast(message, 3000, 'top');
          return of(null);
        })
      )
      .subscribe();
  }

  selectBilling(depositId: number, type: string) {
    this.modalCtrl
      .create({
        component: BillingSelectionComponent,
      })
      .then((modal) => {
        modal.present();
        modal.onDidDismiss().then((event) => {
          if (event.data?.billing?.id && depositId) {
            this.requestInvoice(depositId, event.data.billing.id, type);
          }
        });
      });
  }

  getPayments() {
    this.loading = true;
    return new Promise((resolve) => {
      this.apiService
        .getData('payments?period=' + this.period + '&page=' + this.paymentsPage)
        .then(
          (res) => {
            if (res && res.data) {
              if (res.current_page === 1) {
                this.payments = res.data;
              } else {
                if (this.paymentsLoaded !== res.last_page) {
                  for (const i in res.data) {
                    if (res.data.hasOwnProperty(i)) {
                      this.deposits.push(res.data[i]);
                    }
                  }
                }
              }
              if (res.current_page !== res.last_page) {
                this.paymentsPage += 1;
              }
              this.paymentsLoaded = res.current_page;
            }
            resolve(true);
          },
          () => {
            resolve(false);
          }
        )
        .finally(() => {
          this.loading = false;
        });
    });
  }

  async review(saleId) {
    const modal = await this.modalCtrl.create({
      component: ReviewComponent,
      componentProps: {
        id: null,
        bus_id: null,
        payment_id: null,
        saleId: saleId,
      },
      cssClass: 'modal-fullscreen',
      keyboardClose: true,
      showBackdrop: true,
    });

    return await modal.present();
  }

  getDetails(deposit: Deposit) {
    this.eventService.publish('loading:present');
    this.apiService
      .getData('deposit/' + deposit.id)
      .then((res) => {
        if (res.data) {
          this.showResponse(res.data.method, res.data);
        }
      })
      .finally(() => {
        this.eventService.publish('loading:dismiss');
      });
  }

  goTicket(ticket) {
    if (ticket.type !== 'busticket') {
      return;
    }
    this.dataService.setData(ticket.id, ticket);
    this.router.navigate(['/tecservice-transportec/wallet/transactions/', ticket.id]);
  }

  showResponse(paymentMethod: PaymentMethod, payment: Payment) {
    this.loadingService.present();
    setTimeout(() => {
      this.modalCtrl
        .create({
          component: PaymentConfirmationComponent,
          componentProps: {
            amount: payment.amount,
            method: paymentMethod,
            order: payment.order,
          },
        })
        .then((modal) => {
          modal.present();
          this.loadingService.dismiss();
        });
    }, 1000);
  }
}
